import { cmToFeetInches, cmToInches, kgToLbs } from '@render/utils/unitConversion'
import { formatNumber, type LocaleConfig, type MeasurementSystem } from '@render/lib/localeConfig'
import { localeMonthToken } from '@render/lib/localeMonth'

export type { MeasurementSystem }

// First Strong Isolate / Pop Directional Isolate (U+2068 / U+2069). A locale's
// unit/month word can be strong-RTL (Hebrew, Arabic, …) while the digits next
// to it are weak-LTR — plain concatenation lets the browser's own bidi
// algorithm reposition the word relative to the digits, which silently
// overrides whatever field order these formatters construct (confirmed with a
// conformant UAX#9 implementation: an un-isolated word/number pair collapses
// to the SAME visual output regardless of which order the source string uses,
// even inside a forced `dir="ltr"` container). Wrapping just the word in
// FSI…PDI pins it as its own opaque unit, which — combined with the `<bdi
// dir="ltr">` wrapper at every render site (InterpolationNodeView.vue,
// TipTapRenderer.vue, InterpolationFormatModal.vue) — is what actually makes
// the constructed order stick. No visual effect for Latin-script words (the
// isolate has nothing to isolate from).
const FSI = '⁨'
const PDI = '⁩'
const isolate = (word: string): string => `${FSI}${word}${PDI}`

/**
 * A format ending `_rtl` is a "Force" preset in the Variable Format modal — an
 * author-picked escape hatch that guarantees its exact constructed field order
 * (isolated + wrapped in a forced-`ltr` `<bdi>` at every render site), rather
 * than an automatic per-language behaviour. The stored value keeps its
 * original `_rtl` suffix (existing saved content already uses it); only the
 * modal-facing label reads "Force" now — deliberately, so a language like
 * Arabic that never opts into one of these presets is left entirely to the
 * browser's own bidi resolution, same as before any of this existed.
 */
export function isForcedOrderFormat(format: string | null | undefined): boolean {
  return !!format && format.endsWith('_rtl')
}

export const HEIGHT_WEIGHT_FORMATS = [
  'height_full',
  'height_full_rtl',
  'height_short',
  'height_short_rtl',
  'weight_full',
  'weight_full_rtl',
  'weight_short',
  'weight_short_rtl',
  'weight_short_trim',
  'weight_short_trim_rtl',
  'weight_no_label',
  'length_full',
  'length_full_rtl',
  'length_short',
  'length_short_rtl',
  'length_no_label',
] as const

export type HeightWeightFormat = (typeof HEIGHT_WEIGHT_FORMATS)[number]

export function isHeightWeightFormat(
  format: string | null | undefined,
): format is HeightWeightFormat {
  return !!format && (HEIGHT_WEIGHT_FORMATS as readonly string[]).includes(format)
}

// Strip trailing zeros (and a dangling decimal separator) from a formatted
// number — used by the `*_trim` formats. Separator-agnostic so it works for
// locales using `,` as the decimal mark.
function stripTrailingZeros(formatted: string, decimalSep: string): string {
  const idx = formatted.lastIndexOf(decimalSep)
  if (idx === -1) return formatted
  let end = formatted.length
  while (end > idx + 1 && formatted[end - 1] === '0') end--
  if (end === idx + decimalSep.length) end = idx // drop the lone separator
  return formatted.slice(0, end)
}

/**
 * Format a height, weight, or length interpolation. Source value is assumed
 * metric (cm for height/length, kg for weight). If `system === 'imperial'`,
 * the value is converted to ft+in / lb / in before being rendered.
 *
 * `decimals` is honored for `weight_*` and `length_*` formats (lets callers
 * render sub-kilogram / sub-inch precision for animated interpolation or
 * small fractional deltas). It is ignored for `height_*` formats —
 * `height_full` renders as a `ft + in` pair in imperial, where fractional
 * feet would be confusing.
 *
 * `length_*` is deliberately NOT `height_*`: `height_*` converts through
 * `cmToFeetInches`, which assumes an always-positive person's height and
 * floors non-positive input to `{ft:0, in:0}` — silently wrong for a signed
 * single-axis measurement (e.g. a body-measurement reduction like -3.5cm).
 * `length_*` uses the straight-line, sign-preserving `cmToInches` instead,
 * and (like weight) has no ft component.
 *
 * Config-driven: number separators come from `cfg.number`, and unit labels
 * (short/long for kg/lb/cm/ft/in) come from `cfg.weight` / `cfg.height`, so
 * everything is overridable per (language, geography). See `localeConfig.ts`.
 *
 * The gap between a weight and its unit is `cfg.weight.spaceBetween` (default
 * `true` in every locale → `75 kg` / `165 lb`), a non-breaking space so the
 * pair never wraps apart — same character the currency assembler uses.
 * Height and length have no such switch yet; their full forms keep plain
 * spaces and short forms stay tight (`175cm`, `5ft 9in`, `-1.4in`).
 *
 * Every format has a "Force" counterpart (stored as `height_full_rtl`,
 * `weight_short_rtl`, `length_short_rtl`, … — the modal shows it as "- Force",
 * see `isForcedOrderFormat`) that puts the unit BEFORE the number/value
 * instead of after, isolated so the order actually sticks. It's an explicit
 * author opt-in, not automatic per-language behaviour: the plain preset is
 * left completely untouched — no isolation, no forced `<bdi>` — so a
 * language that never selects "Force" is rendered exactly as it always was,
 * fully trusting the browser's own bidi resolution. `weight_no_label` /
 * `length_no_label` have no Force counterpart — no unit to reorder.
 */
export function formatHeightWeight(
  value: unknown,
  format: HeightWeightFormat,
  system: MeasurementSystem,
  cfg: LocaleConfig,
  decimals: number | null | undefined = null,
): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')

  // Shared by weight_* (sub-kilogram) and length_* (sub-inch/cm) branches —
  // height_* ignores it, ft/in fractions would be confusing.
  const digits = decimals ?? 0
  const sep = cfg.number.decimalSeparator
  // U+00A0 — keeps `75` and `kg` on one line, as `assembleCurrency` does.
  const wGap = cfg.weight.spaceBetween ? '\u00a0' : ''
  const force = isForcedOrderFormat(format)
  const assemble = (numStr: string, unit: string, gap: string): string => {
    const word = force ? isolate(unit) : unit
    return force ? `${word}${gap}${numStr}` : `${numStr}${gap}${word}`
  }

  switch (format) {
    case 'height_full':
    case 'height_full_rtl':
      if (system === 'imperial') {
        const { ft, in: inches } = cmToFeetInches(num)
        return `${assemble(formatNumber(ft, cfg, 0), cfg.height.foot.long, ' ')} ${assemble(formatNumber(inches, cfg, 0), cfg.height.inch.long, ' ')}`
      }
      return assemble(formatNumber(num, cfg, 0), cfg.height.centimeter.long, ' ')

    case 'height_short':
    case 'height_short_rtl':
      if (system === 'imperial') {
        const { ft, in: inches } = cmToFeetInches(num)
        return `${assemble(formatNumber(ft, cfg, 0), cfg.height.foot.short, '')} ${assemble(formatNumber(inches, cfg, 0), cfg.height.inch.short, '')}`
      }
      return assemble(formatNumber(num, cfg, 0), cfg.height.centimeter.short, '')

    case 'weight_full':
    case 'weight_full_rtl':
      if (system === 'imperial') {
        return assemble(formatNumber(kgToLbs(num), cfg, digits), cfg.weight.pound.long, wGap)
      }
      return assemble(formatNumber(num, cfg, digits), cfg.weight.kilogram.long, wGap)

    case 'weight_short':
    case 'weight_short_rtl':
      if (system === 'imperial') {
        return assemble(formatNumber(kgToLbs(num), cfg, digits), cfg.weight.pound.short, wGap)
      }
      return assemble(formatNumber(num, cfg, digits), cfg.weight.kilogram.short, wGap)

    case 'weight_short_trim':
    case 'weight_short_trim_rtl':
      if (system === 'imperial') {
        return assemble(
          stripTrailingZeros(formatNumber(kgToLbs(num), cfg, digits), sep),
          cfg.weight.pound.short,
          wGap,
        )
      }
      return assemble(
        stripTrailingZeros(formatNumber(num, cfg, digits), sep),
        cfg.weight.kilogram.short,
        wGap,
      )

    case 'weight_no_label':
      if (system === 'imperial') {
        return formatNumber(kgToLbs(num), cfg, digits)
      }
      return formatNumber(num, cfg, digits)

    // Straight-line signed cm/in — see the function doc for why this isn't
    // height_* (cmToFeetInches floors non-positive input to zero).
    case 'length_full':
    case 'length_full_rtl':
      if (system === 'imperial') {
        return assemble(formatNumber(cmToInches(num), cfg, digits), cfg.height.inch.long, ' ')
      }
      return assemble(formatNumber(num, cfg, digits), cfg.height.centimeter.long, ' ')

    case 'length_short':
    case 'length_short_rtl':
      if (system === 'imperial') {
        return assemble(formatNumber(cmToInches(num), cfg, digits), cfg.height.inch.short, '')
      }
      return assemble(formatNumber(num, cfg, digits), cfg.height.centimeter.short, '')

    case 'length_no_label':
      if (system === 'imperial') {
        return formatNumber(cmToInches(num), cfg, digits)
      }
      return formatNumber(num, cfg, digits)
  }
}

export const DURATION_FORMATS = ['duration_mm_ss'] as const

export type DurationFormat = (typeof DURATION_FORMATS)[number]

export function isDurationFormat(format: string | null | undefined): format is DurationFormat {
  return !!format && (DURATION_FORMATS as readonly string[]).includes(format)
}

export function formatDuration(value: unknown, format: DurationFormat): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  const secs = Math.max(0, Math.floor(num))

  switch (format) {
    case 'duration_mm_ss': {
      const mm = Math.floor(secs / 60)
      const ss = secs % 60
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    }
  }
}

export const PERIOD_FORMATS = ['period_singular', 'period_plural'] as const

export type PeriodFormat = (typeof PERIOD_FORMATS)[number]

export function isPeriodFormat(format: string | null | undefined): format is PeriodFormat {
  return !!format && (PERIOD_FORMATS as readonly string[]).includes(format)
}

/**
 * Format a period-unit interpolation as its translated word. `value` is the
 * unit string ('minute' | 'day' | 'week' | 'month' | 'year'). `resolveTranslation`
 * looks up `tr.global.period.in.<unit>` / `<unit>s` from the pipe store; the
 * `global.` prefix is required because the runtime translations map is keyed
 * by `${group}.${name}` (see PipeFullReleaseDto). Callers pass `resolveTranslation`
 * in because this module stays free of pipe-store coupling (every other
 * formatter here is pure too). Falls back to the raw unit (or unit+'s') when
 * no translation is registered — note that fallback is English-shaped, so for
 * non-English locales the only correct path is the translation hit.
 */
export function formatPeriod(
  value: unknown,
  format: PeriodFormat,
  resolveTranslation: (key: string) => string | undefined,
): string {
  if (value === null || value === undefined || value === '') return ''
  const unit = String(value).toLowerCase()
  const trKey =
    format === 'period_plural' ? `tr.global.period.in.${unit}s` : `tr.global.period.in.${unit}`
  const fallback = format === 'period_plural' ? `${unit}s` : unit
  return resolveTranslation(trKey) ?? fallback
}

export const DATE_FORMATS = [
  'date_dmy_abbr',
  'date_dmy_abbr_rtl',
  'date_dmy_no_year',
  'date_dmy_no_year_rtl',
  'date_dmy_caps_dash',
  'date_dmy_caps_dash_rtl',
] as const

export type DateFormat = (typeof DATE_FORMATS)[number]

export function isDateFormat(format: string | null | undefined): format is DateFormat {
  return !!format && (DATE_FORMATS as readonly string[]).includes(format)
}

const DATE_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()
function dateFormatter(locale: string): Intl.DateTimeFormat {
  let f = DATE_FORMATTER_CACHE.get(locale)
  if (!f) {
    // timeZone: 'UTC' so a YYYY-MM-DD seed (parsed as UTC midnight by `new
    // Date()`) doesn't drift by a day in non-UTC display zones — date
    // variables here represent calendar dates, not instants.
    f = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    DATE_FORMATTER_CACHE.set(locale, f)
  }
  return f
}

/**
 * Format a date interpolation as `1 Jan 2026` — capitalised abbreviated
 * month, space-separated, locale-aware month names. Accepts ISO-8601 strings
 * (date or datetime), ms timestamps, Date instances, or anything `new Date()`
 * parses. Returns the raw value as a string when unparseable.
 *
 * Day and year come from the full-date formatter (right numbering system and
 * zero-padding conventions); the month goes through `localeMonthToken`, because
 * the abbreviation a full-date request returns is NUMERIC in cs / sk / ja / lt —
 * `1 6 2026`, month indistinguishable from day. See `localeMonth.ts`.
 *
 * Every format has a "Force" counterpart (stored as `date_dmy_abbr_rtl`, …
 * — see `isForcedOrderFormat`) that reverses the field order (`year month
 * day`, `month day`, `year-MONTH-day`), isolated so the order actually
 * sticks. Explicit author opt-in, not automatic per-language behaviour: the
 * plain preset is left completely untouched — no isolation, no forced
 * `<bdi>` — so a language that never selects "Force" renders exactly as it
 * always did, fully trusting the browser's own bidi resolution.
 */
export function formatDate(value: unknown, format: DateFormat, locale: string = 'en-EN'): string {
  if (value === null || value === undefined || value === '') return ''
  const dt = value instanceof Date ? value : new Date(value as string | number)
  if (isNaN(dt.getTime())) return String(value)

  const parts = dateFormatter(locale).formatToParts(dt)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  // Already trailing-dot-stripped and never lowercased — `Intl` provides the
  // locale's preferred capitalisation.
  const rawMonth = localeMonthToken(locale, dt, get('month'))
  const rtl = isForcedOrderFormat(format)
  const month = rtl ? isolate(rawMonth) : rawMonth

  switch (format) {
    case 'date_dmy_abbr':
    case 'date_dmy_abbr_rtl':
      return rtl ? `${get('year')} ${month} ${get('day')}` : `${get('day')} ${month} ${get('year')}`
    case 'date_dmy_no_year':
    case 'date_dmy_no_year_rtl':
      return rtl ? `${month} ${get('day')}` : `${get('day')} ${month}`
    case 'date_dmy_caps_dash':
    case 'date_dmy_caps_dash_rtl': {
      // `toLocaleUpperCase(locale)` so non-ASCII abbreviations capitalise under
      // the active locale's casing rules (Latvian "jūn" → "JŪN"). Isolate,
      // then uppercase — `.toLocaleUpperCase` doesn't touch the FSI/PDI marks.
      const MONTH = month.toLocaleUpperCase(locale)
      return rtl ? `${get('year')}-${MONTH}-${get('day')}` : `${get('day')}-${MONTH}-${get('year')}`
    }
  }
}

export const TEXT_CASE_FORMATS = ['all_caps'] as const

export type TextCaseFormat = (typeof TEXT_CASE_FORMATS)[number]

export function isTextCaseFormat(format: string | null | undefined): format is TextCaseFormat {
  return !!format && (TEXT_CASE_FORMATS as readonly string[]).includes(format)
}

export function formatTextCase(
  value: unknown,
  format: TextCaseFormat,
  locale: string = 'en-EN',
): string {
  if (value === null || value === undefined || value === '') return ''
  switch (format) {
    case 'all_caps':
      return String(value).toLocaleUpperCase(locale)
  }
}
